/**
 * ClientInvoiceDrawer Component
 * Create / Edit Payment Application (AIA G702/G703)
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  DollarSign,
  FileText,
  Hash,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Controller, FieldValues, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import {
  useCreateClientInvoiceMutation,
  useUpdateClientInvoiceMutation,
} from '../../services/clientInvoiceApi';
import type {
  CreatePaymentApplicationRequest,
  PaymentApplication,
  SOVLineItemRequest,
  UpdatePaymentApplicationRequest,
} from '../../types/clientInvoice';
import { CurrencyInput, DatePicker, Input, NumberInput, TextArea } from '../forms';

// ============================================================================
// PROPS
// ============================================================================

export interface ClientInvoiceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  application?: PaymentApplication | null;
  mode: 'create' | 'edit';
  onSuccess?: () => void;
}

// ============================================================================
// FORM TYPES
// ============================================================================

interface SOVLineFormItem {
  _id?: string;
  itemNumber: string;
  description: string;
  scheduledValue: number;
  previousApplications: number;
  thisPeriodWorkCompleted: number;
  materialStored: number;
}

interface ClientInvoiceFormData {
  billingPeriodFrom: string;
  billingPeriodTo: string;
  originalContractSum: number;
  netChangeByChangeOrders: number;
  lessPreviousCertificates: number;
  retainagePercent: number;
  notes: string;
  sovLineItems: SOVLineFormItem[];
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

const sovLineSchema = Yup.object({
  itemNumber: Yup.string().required('Item number is required').max(50),
  description: Yup.string().required('Description is required').max(500),
  scheduledValue: Yup.number()
    .min(0, 'Cannot be negative')
    .required('Scheduled value is required'),
  previousApplications: Yup.number().min(0, 'Cannot be negative').required(),
  thisPeriodWorkCompleted: Yup.number().min(0, 'Cannot be negative').required(),
  materialStored: Yup.number().min(0, 'Cannot be negative').required(),
});

const validationSchema = Yup.object({
  billingPeriodFrom: Yup.string().required('Billing period start is required'),
  billingPeriodTo: Yup.string().required('Billing period end is required'),
  originalContractSum: Yup.number()
    .min(0, 'Cannot be negative')
    .required('Original contract sum is required'),
  netChangeByChangeOrders: Yup.number().required(),
  lessPreviousCertificates: Yup.number().min(0, 'Cannot be negative').required(),
  retainagePercent: Yup.number()
    .min(0, 'Cannot be negative')
    .max(100, 'Cannot exceed 100%')
    .required('Retainage percent is required'),
  notes: Yup.string().optional().max(5000),
  sovLineItems: Yup.array().of(sovLineSchema).optional(),
});

// ============================================================================
// HELPERS
// ============================================================================

const formatCurrencyDisplay = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

// Compute totals from form values for display preview
function computeSOVTotals(
  lines: SOVLineFormItem[],
  retainagePct: number
): {
  totalScheduled: number;
  totalThisPeriod: number;
  totalCompleted: number;
  totalRetainage: number;
} {
  const totalScheduled = lines.reduce((s, l) => s + (Number(l.scheduledValue) || 0), 0);
  const totalCompleted = lines.reduce(
    (s, l) =>
      s +
      (Number(l.previousApplications) || 0) +
      (Number(l.thisPeriodWorkCompleted) || 0) +
      (Number(l.materialStored) || 0),
    0
  );
  const totalThisPeriod = lines.reduce(
    (s, l) => s + (Number(l.thisPeriodWorkCompleted) || 0) + (Number(l.materialStored) || 0),
    0
  );
  const totalRetainage = Math.round(totalCompleted * (retainagePct / 100) * 100) / 100;
  return { totalScheduled, totalThisPeriod, totalCompleted, totalRetainage };
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ClientInvoiceDrawer: React.FC<ClientInvoiceDrawerProps> = ({
  isOpen,
  onClose,
  application,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  const [createClientInvoice, { isLoading: isCreating }] = useCreateClientInvoiceMutation();
  const [updateClientInvoice, { isLoading: isUpdating }] = useUpdateClientInvoiceMutation();

  const isLoading = isCreating || isUpdating;

  // --------------------------------------------------------------------------
  // FORM SETUP
  // --------------------------------------------------------------------------

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ClientInvoiceFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      billingPeriodFrom: '',
      billingPeriodTo: '',
      originalContractSum: 0,
      netChangeByChangeOrders: 0,
      lessPreviousCertificates: 0,
      retainagePercent: 10,
      notes: '',
      sovLineItems: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sovLineItems',
  });

  // Watch for live summary preview
  const watchedLines = watch('sovLineItems');
  const watchedRetainagePct = watch('retainagePercent');
  const watchedOriginalSum = watch('originalContractSum');
  const watchedNetChange = watch('netChangeByChangeOrders');
  const watchedLessPrev = watch('lessPreviousCertificates');

  // --------------------------------------------------------------------------
  // POPULATE FORM ON EDIT
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'edit' && application) {
      reset({
        billingPeriodFrom: application.billingPeriodFrom
          ? application.billingPeriodFrom.split('T')[0]
          : '',
        billingPeriodTo: application.billingPeriodTo
          ? application.billingPeriodTo.split('T')[0]
          : '',
        originalContractSum: application.originalContractSum || 0,
        netChangeByChangeOrders: application.netChangeByChangeOrders || 0,
        lessPreviousCertificates: application.lessPreviousCertificates || 0,
        retainagePercent: application.retainagePercent ?? 10,
        notes: application.notes || '',
        sovLineItems: application.sovLineItems.map(item => ({
          _id: item._id,
          itemNumber: item.itemNumber,
          description: item.description,
          scheduledValue: item.scheduledValue,
          previousApplications: item.previousApplications,
          thisPeriodWorkCompleted: item.thisPeriodWorkCompleted,
          materialStored: item.materialStored,
        })),
      });
    } else {
      reset({
        billingPeriodFrom: '',
        billingPeriodTo: '',
        originalContractSum: 0,
        netChangeByChangeOrders: 0,
        lessPreviousCertificates: 0,
        retainagePercent: 10,
        notes: '',
        sovLineItems: [],
      });
    }
  }, [isOpen, mode, application, reset]);

  // --------------------------------------------------------------------------
  // ADD SOV LINE
  // --------------------------------------------------------------------------

  const handleAddLine = useCallback(() => {
    const nextNum = fields.length + 1;
    append({
      itemNumber: String(nextNum).padStart(2, '0'),
      description: '',
      scheduledValue: 0,
      previousApplications: 0,
      thisPeriodWorkCompleted: 0,
      materialStored: 0,
    });
  }, [append, fields.length]);

  // --------------------------------------------------------------------------
  // SUBMIT
  // --------------------------------------------------------------------------

  const onSubmit = async (data: ClientInvoiceFormData) => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    const sovLineItems: SOVLineItemRequest[] = data.sovLineItems.map(item => ({
      _id: item._id,
      itemNumber: item.itemNumber,
      description: item.description,
      scheduledValue: Number(item.scheduledValue),
      previousApplications: Number(item.previousApplications),
      thisPeriodWorkCompleted: Number(item.thisPeriodWorkCompleted),
      materialStored: Number(item.materialStored),
    }));

    if (mode === 'create') {
      const payload: CreatePaymentApplicationRequest = {
        billingPeriodFrom: data.billingPeriodFrom,
        billingPeriodTo: data.billingPeriodTo,
        originalContractSum: Number(data.originalContractSum),
        netChangeByChangeOrders: Number(data.netChangeByChangeOrders),
        lessPreviousCertificates: Number(data.lessPreviousCertificates),
        retainagePercent: Number(data.retainagePercent),
        notes: data.notes || undefined,
        sovLineItems,
      };

      const result = await createClientInvoice({ projectId, data: payload });

      if ('error' in result) {
        toast.error('Failed to create payment application');
        return;
      }

      toast.success('Payment application created successfully');
    } else if (mode === 'edit' && application) {
      const payload: UpdatePaymentApplicationRequest = {
        billingPeriodFrom: data.billingPeriodFrom,
        billingPeriodTo: data.billingPeriodTo,
        originalContractSum: Number(data.originalContractSum),
        netChangeByChangeOrders: Number(data.netChangeByChangeOrders),
        lessPreviousCertificates: Number(data.lessPreviousCertificates),
        retainagePercent: Number(data.retainagePercent),
        notes: data.notes || undefined,
        sovLineItems,
      };

      const result = await updateClientInvoice({
        projectId,
        applicationId: application._id,
        data: payload,
      });

      if ('error' in result) {
        toast.error('Failed to update payment application');
        return;
      }

      toast.success('Payment application updated successfully');
    }

    onSuccess?.();
    onClose();
  };

  // --------------------------------------------------------------------------
  // LIVE SUMMARY PREVIEW
  // --------------------------------------------------------------------------

  const sovTotals = computeSOVTotals(watchedLines || [], Number(watchedRetainagePct) || 0);
  const contractSumToDate =
    (Number(watchedOriginalSum) || 0) + (Number(watchedNetChange) || 0);
  const totalEarnedLessRetainage = sovTotals.totalCompleted - sovTotals.totalRetainage;
  const currentPaymentDue = totalEarnedLessRetainage - (Number(watchedLessPrev) || 0);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer — 90% width (inset-6) for complex multi-section form */}
      <div className="fixed top-6 right-6 bottom-6 left-6 z-[100] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-9 h-9 bg-brand-50 rounded-lg">
                <FileText className="h-5 w-5 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create' ? 'New Payment Application' : 'Edit Payment Application'}
                </h2>
                <p className="text-xs text-gray-500">AIA G702 / G703 Billing</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <form
            onSubmit={handleSubmit(onSubmit as (data: FieldValues) => void)}
            id="client-invoice-form"
            className="flex-1 overflow-y-auto"
          >
            <div className="px-8 py-6 space-y-8">

              {/* ---- SECTION 1: Application Details ---- */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
                  <Hash className="h-4 w-4 text-brand-600" />
                  <span>Application Details</span>
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Billing Period From */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Billing Period From <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="billingPeriodFrom"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select start date"
                          error={errors.billingPeriodFrom?.message}
                        />
                      )}
                    />
                  </div>

                  {/* Billing Period To */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Billing Period To <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="billingPeriodTo"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select end date"
                          error={errors.billingPeriodTo?.message}
                        />
                      )}
                    />
                  </div>

                  {/* Retainage Percent */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Retainage % <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="retainagePercent"
                      control={control}
                      render={({ field }) => (
                        <NumberInput
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="10"
                          min={0}
                          max={100}
                          decimalPlaces={2}
                          error={errors.retainagePercent?.message}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder="Optional notes for this billing period..."
                        rows={2}
                        error={errors.notes?.message}
                      />
                    )}
                  />
                </div>
              </section>

              {/* ---- SECTION 2: Contract Summary (G702) ---- */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
                  <DollarSign className="h-4 w-4 text-brand-600" />
                  <span>Contract Summary (AIA G702)</span>
                </h3>

                <div className="grid grid-cols-3 gap-4">
                  {/* Original Contract Sum */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Original Contract Sum <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="originalContractSum"
                      control={control}
                      render={({ field }) => (
                        <CurrencyInput
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="$0.00"
                          error={errors.originalContractSum?.message}
                        />
                      )}
                    />
                  </div>

                  {/* Net Change by Change Orders */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Net Change by Change Orders
                    </label>
                    <Controller
                      name="netChangeByChangeOrders"
                      control={control}
                      render={({ field }) => (
                        <CurrencyInput
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="$0.00"
                          error={errors.netChangeByChangeOrders?.message}
                        />
                      )}
                    />
                  </div>

                  {/* Less Previous Certificates */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Less Previous Certificates
                    </label>
                    <Controller
                      name="lessPreviousCertificates"
                      control={control}
                      render={({ field }) => (
                        <CurrencyInput
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="$0.00"
                          error={errors.lessPreviousCertificates?.message}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Live Summary Preview */}
                <div className="mt-4 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-200 bg-gray-100">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Calculated Summary
                    </p>
                  </div>
                  <div className="px-5 py-4 space-y-2">
                    {[
                      { label: 'Contract Sum to Date', value: contractSumToDate },
                      { label: 'Total Completed & Stored', value: sovTotals.totalCompleted },
                      { label: `Retainage (${watchedRetainagePct ?? 10}%)`, value: -sovTotals.totalRetainage },
                      { label: 'Total Earned Less Retainage', value: totalEarnedLessRetainage },
                      { label: 'Less Previous Certificates', value: -(Number(watchedLessPrev) || 0) },
                    ].map(row => (
                      <div key={row.label} className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{row.label}</span>
                        <span className="font-medium text-gray-700">
                          {formatCurrencyDisplay(row.value)}
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-gray-300 pt-2 flex items-center justify-between text-sm">
                      <span className="font-semibold text-gray-700">Current Payment Due</span>
                      <span className="font-bold text-brand-600">
                        {formatCurrencyDisplay(currentPaymentDue)}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* ---- SECTION 3: Schedule of Values (G703) ---- */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-brand-600" />
                    <span>Schedule of Values (AIA G703)</span>
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {fields.length} {fields.length === 1 ? 'line' : 'lines'}
                    </span>
                  </h3>
                  <button
                    type="button"
                    onClick={handleAddLine}
                    className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                    <span>Add Line</span>
                  </button>
                </div>

                {fields.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                    <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No SOV lines added yet</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Click "Add Line" to build your Schedule of Values
                    </p>
                    <button
                      type="button"
                      onClick={handleAddLine}
                      className="mt-3 px-4 py-2 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors"
                    >
                      Add First Line
                    </button>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Table Header */}
                    <div className="grid grid-cols-[3rem_1fr_2fr_1fr_1fr_1fr_1fr_2.5rem] gap-0 bg-gray-50 border-b border-gray-200">
                      {[
                        '#',
                        'Item No.',
                        'Description',
                        'Sched Value',
                        'Prev Apps',
                        'This Period',
                        'Mat Stored',
                        '',
                      ].map((col, i) => (
                        <div
                          key={i}
                          className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                        >
                          {col}
                        </div>
                      ))}
                    </div>

                    {/* Table Rows */}
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="grid grid-cols-[3rem_1fr_2fr_1fr_1fr_1fr_1fr_2.5rem] gap-0 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50"
                      >
                        {/* Row Number */}
                        <div className="flex items-center justify-center px-2 text-xs text-gray-400 font-medium">
                          {index + 1}
                        </div>

                        {/* Item Number */}
                        <div className="px-2 py-2">
                          <input
                            {...register(`sovLineItems.${index}.itemNumber`)}
                            type="text"
                            className="w-full h-8 px-2 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                            placeholder="01"
                          />
                          {errors.sovLineItems?.[index]?.itemNumber && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {errors.sovLineItems[index]?.itemNumber?.message}
                            </p>
                          )}
                        </div>

                        {/* Description */}
                        <div className="px-2 py-2">
                          <input
                            {...register(`sovLineItems.${index}.description`)}
                            type="text"
                            className="w-full h-8 px-2 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                            placeholder="Work description..."
                          />
                          {errors.sovLineItems?.[index]?.description && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {errors.sovLineItems[index]?.description?.message}
                            </p>
                          )}
                        </div>

                        {/* Scheduled Value */}
                        <div className="px-2 py-2">
                          <input
                            {...register(`sovLineItems.${index}.scheduledValue`, {
                              valueAsNumber: true,
                            })}
                            type="number"
                            min={0}
                            step="0.01"
                            className="w-full h-8 px-2 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-right"
                            placeholder="0.00"
                          />
                        </div>

                        {/* Previous Applications */}
                        <div className="px-2 py-2">
                          <input
                            {...register(`sovLineItems.${index}.previousApplications`, {
                              valueAsNumber: true,
                            })}
                            type="number"
                            min={0}
                            step="0.01"
                            className="w-full h-8 px-2 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-right"
                            placeholder="0.00"
                          />
                        </div>

                        {/* This Period Work Completed */}
                        <div className="px-2 py-2">
                          <input
                            {...register(`sovLineItems.${index}.thisPeriodWorkCompleted`, {
                              valueAsNumber: true,
                            })}
                            type="number"
                            min={0}
                            step="0.01"
                            className="w-full h-8 px-2 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-right"
                            placeholder="0.00"
                          />
                        </div>

                        {/* Material Stored */}
                        <div className="px-2 py-2">
                          <input
                            {...register(`sovLineItems.${index}.materialStored`, {
                              valueAsNumber: true,
                            })}
                            type="number"
                            min={0}
                            step="0.01"
                            className="w-full h-8 px-2 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-right"
                            placeholder="0.00"
                          />
                        </div>

                        {/* Delete Row */}
                        <div className="flex items-center justify-center px-1">
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            aria-label={`Remove line ${index + 1}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* SOV Totals Row */}
                    <div className="grid grid-cols-[3rem_1fr_2fr_1fr_1fr_1fr_1fr_2.5rem] gap-0 bg-brand-50 border-t border-brand-100">
                      <div className="px-3 py-2 text-xs font-bold text-brand-700 col-span-3">
                        Totals
                      </div>
                      <div className="px-3 py-2 text-xs font-bold text-brand-700 text-right">
                        {formatCurrencyDisplay(sovTotals.totalScheduled)}
                      </div>
                      <div className="px-3 py-2 text-xs text-brand-600 text-right">—</div>
                      <div className="px-3 py-2 text-xs font-bold text-brand-700 text-right">
                        {formatCurrencyDisplay(sovTotals.totalThisPeriod)}
                      </div>
                      <div className="px-3 py-2 text-xs text-brand-600 text-right">—</div>
                      <div />
                    </div>
                  </div>
                )}
              </section>

            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-between flex-shrink-0">
            <div className="text-xs text-gray-500">
              {fields.length > 0 && (
                <span>
                  {fields.length} SOV {fields.length === 1 ? 'line' : 'lines'} &bull; Scheduled:{' '}
                  {formatCurrencyDisplay(sovTotals.totalScheduled)}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="client-invoice-form"
                disabled={isLoading}
                className="px-5 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading
                  ? mode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : mode === 'create'
                    ? 'Save Draft'
                    : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ClientInvoiceDrawer;
